"use client"

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin"
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { mergeRegister } from "@lexical/utils"
import { Input, Label } from "@ui/shad"
import { cn } from "@ui/utils"
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  COMMAND_PRIORITY_HIGH,
  EditorState,
  KEY_ENTER_COMMAND,
  LexicalEditor,
  LexicalNode,
  LineBreakNode,
  RootNode,
} from "lexical"

import { shouldShowPlaceholderFromRoot } from "@/components/lexical/core/placeholder-visibility"
import {
  $createPlaceholderNode,
  $isPlaceholderNode,
  PlaceholderNode,
} from "@/components/lexical/nodes/placeholder/node"
import {
  findPlaceholderByNormalizedValue,
  LIQUID_PLACEHOLDER_REGEX,
  PlaceholderData,
  registerPlaceholders,
} from "@/components/lexical/placeholders"
import PlaceholderPlugin from "@/components/lexical/plugins/placeholder"

export interface EmailSubjectFieldHandle {
  focus: () => void
  getEditor: () => LexicalEditor | null
}

interface EmailSubjectFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  autoFocus?: boolean
  disabled?: boolean
  showLabel?: boolean
  className?: string
  inputClassName?: string
  // Placeholder support
  enablePlaceholders?: boolean
  placeholders?: PlaceholderData[]
}

// Simple input version (no placeholders)
function SimpleSubjectInput({
  value,
  onChange,
  placeholder = "Add a subject",
  autoFocus = false,
  disabled = false,
  showLabel = false,
  className,
  inputClassName,
}: Omit<EmailSubjectFieldProps, "enablePlaceholders" | "placeholders">) {
  return (
    <div className={cn("flex flex-none items-center border-b", className)}>
      {showLabel && (
        <Label htmlFor="email-subject" className="text-muted-foreground pl-2 text-xs font-normal">
          Subject
        </Label>
      )}
      <Input
        id="email-subject"
        autoFocus={autoFocus}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "placeholder:text-input-placeholder rounded-none border-0 p-0 px-4 font-medium shadow-none focus-visible:ring-0",
          showLabel && "pl-2",
          inputClassName
        )}
        disabled={disabled}
      />
    </div>
  )
}

// Lexical-based version with placeholder support
function CaptureEditorPlugin({ setEditor }: { setEditor: (editor: LexicalEditor) => void }) {
  const [lexicalEditor] = useLexicalComposerContext()
  useEffect(() => {
    setEditor(lexicalEditor)
  }, [lexicalEditor, setEditor])
  return null
}

function SingleLinePlugin() {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          event?.preventDefault()
          return true
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerNodeTransform(LineBreakNode, (node) => {
        node.remove()
      }),
      editor.registerNodeTransform(RootNode, (root) => {
        const [firstBlock, ...extraBlocks] = root.getChildren()
        if (!$isElementNode(firstBlock)) return

        for (const block of extraBlocks) {
          if ($isElementNode(block) && block.getChildrenSize() > 0) {
            firstBlock.append($createTextNode(" "), ...block.getChildren())
          }
          block.remove()
        }
      })
    )
  }, [editor])

  return null
}

function parseTextWithPlaceholders(text: string, placeholders: PlaceholderData[]): LexicalNode[] {
  const nodes: LexicalNode[] = []
  let lastIndex = 0

  // Reset regex state
  const regex = new RegExp(LIQUID_PLACEHOLDER_REGEX.source, "g")

  let match
  while ((match = regex.exec(text)) !== null) {
    // Add text before the placeholder
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index)
      nodes.push($createTextNode(textBefore))
    }

    // Find the placeholder definition
    const placeholderValue = match[0]
    const definition = findPlaceholderByNormalizedValue(placeholderValue, placeholders)

    if (definition) {
      nodes.push($createPlaceholderNode(definition.label, definition.value, definition.id, definition.description))
    } else {
      // If no definition found, keep as text
      nodes.push($createTextNode(placeholderValue))
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text after the last placeholder
  if (lastIndex < text.length) {
    nodes.push($createTextNode(text.slice(lastIndex)))
  }

  return nodes
}

// Variables can be registered after the subject is parsed (e.g. async form fields), so convert
// any raw Liquid that now matches into chips without touching the rest of the content.
function $restorePlaceholderChips(placeholders: PlaceholderData[]) {
  for (const textNode of $getRoot().getAllTextNodes()) {
    const text = textNode.getTextContent()
    if (!text.includes("{{")) continue

    const nodes = parseTextWithPlaceholders(text, placeholders)
    if (!nodes.some($isPlaceholderNode)) continue

    textNode.replace(nodes[0])
    nodes.slice(1).reduce((previous, node) => previous.insertAfter(node), nodes[0])
  }
}

function PlaceholderSubjectEditorInner({
  value,
  onChange,
  placeholder = "Add a subject",
  placeholders = [],
  className,
  autoFocus,
  disabled,
  editorRef,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  placeholders?: PlaceholderData[]
  className?: string
  autoFocus: boolean
  disabled: boolean
  editorRef: React.MutableRefObject<LexicalEditor | null>
}) {
  const [editor] = useLexicalComposerContext()
  const [showPlaceholder, setShowPlaceholder] = useState(true)
  const skipNextChangeRef = useRef(false)
  const lastValueRef = useRef(value)

  if (placeholders.length > 0) {
    registerPlaceholders(placeholders)
  }

  useEffect(() => {
    editor.setEditable(!disabled)
  }, [editor, disabled])

  useEffect(() => {
    editor.update(() => $restorePlaceholderChips(placeholders), { tag: "history-merge" })
  }, [editor, placeholders])

  // Track placeholder visibility
  useEffect(() => {
    const checkIfEmpty = () => {
      editor.getEditorState().read(() => {
        const root = $getRoot()
        setShowPlaceholder(shouldShowPlaceholderFromRoot(root))
      })
    }

    checkIfEmpty()

    const unregister = editor.registerUpdateListener(() => {
      checkIfEmpty()
    })

    return unregister
  }, [editor])

  const handleChange = useCallback(
    (editorState: EditorState) => {
      if (skipNextChangeRef.current) {
        skipNextChangeRef.current = false
        return
      }

      editorState.read(() => {
        const root = $getRoot()
        const text = root.getTextContent()
        if (text !== lastValueRef.current) {
          lastValueRef.current = text
          onChange(text)
        }
      })
    },
    [onChange]
  )

  // Sync external value changes to editor
  useEffect(() => {
    if (!editorRef.current || value === lastValueRef.current) return

    lastValueRef.current = value
    skipNextChangeRef.current = true

    editorRef.current.update(() => {
      const root = $getRoot()
      root.clear()
      const paragraph = $createParagraphNode()
      if (value) {
        const nodes = parseTextWithPlaceholders(value, placeholders)
        if (nodes.length > 0) {
          paragraph.append(...nodes)
        }
      }
      root.append(paragraph)
    })
  }, [value, editorRef, placeholders])

  return (
    <div className="relative flex-1">
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className={cn("min-h-[20px] w-full text-sm outline-none", "font-medium", "[&_p]:m-0", className)}
          />
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      {showPlaceholder && (
        <div className="text-muted-foreground pointer-events-none absolute inset-0 text-sm font-medium">
          {placeholder}
        </div>
      )}
      <HistoryPlugin />
      <SingleLinePlugin />
      <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      <CaptureEditorPlugin setEditor={(e) => (editorRef.current = e)} />
      {autoFocus && <AutoFocusPlugin defaultSelection="rootEnd" />}
      {placeholders.length > 0 && !disabled && <PlaceholderPlugin placeholders={placeholders} />}
    </div>
  )
}

function PlaceholderSubjectInput(
  {
    value,
    onChange,
    placeholder = "Add a subject",
    placeholders = [],
    className,
    inputClassName,
    showLabel = false,
    autoFocus = false,
    disabled = false,
  }: Omit<EmailSubjectFieldProps, "enablePlaceholders"> & {
    placeholders: PlaceholderData[]
  },
  ref: React.Ref<EmailSubjectFieldHandle>
) {
  const editorRef = useRef<LexicalEditor | null>(null)

  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    getEditor: () => editorRef.current,
  }))

  useEffect(() => {
    if (placeholders.length > 0) {
      registerPlaceholders(placeholders)
    }
  }, [placeholders])

  const initialConfig: InitialConfigType = {
    namespace: "EmailSubjectField",
    onError: (error) => console.error(error),
    nodes: [PlaceholderNode],
    editable: !disabled,
    editorState: value
      ? () => {
          const root = $getRoot()
          const paragraph = $createParagraphNode()
          const nodes = parseTextWithPlaceholders(value, placeholders)
          if (nodes.length > 0) {
            paragraph.append(...nodes)
          }
          root.append(paragraph)
        }
      : undefined,
  }

  return (
    <div className={cn("relative flex flex-none items-center border-b px-4", className)}>
      {showLabel && (
        <Label htmlFor="email-subject" className="text-muted-foreground pr-2 text-xs font-normal">
          Subject
        </Label>
      )}
      <LexicalComposer initialConfig={initialConfig}>
        <PlaceholderSubjectEditorInner
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          placeholders={placeholders}
          className={inputClassName}
          autoFocus={autoFocus}
          disabled={disabled}
          editorRef={editorRef}
        />
      </LexicalComposer>
    </div>
  )
}

const PlaceholderSubjectInputWithRef = forwardRef<
  EmailSubjectFieldHandle,
  Omit<EmailSubjectFieldProps, "enablePlaceholders"> & { placeholders: PlaceholderData[] }
>(PlaceholderSubjectInput)

// Main component that switches between simple and placeholder versions
function EmailSubjectFieldComponent(
  { enablePlaceholders = false, placeholders, ...props }: EmailSubjectFieldProps,
  ref: React.Ref<EmailSubjectFieldHandle>
) {
  if (enablePlaceholders && placeholders && placeholders.length > 0) {
    return <PlaceholderSubjectInputWithRef ref={ref} {...props} placeholders={placeholders} />
  }

  return <SimpleSubjectInput {...props} />
}

export const EmailSubjectField = forwardRef<EmailSubjectFieldHandle, EmailSubjectFieldProps>(EmailSubjectFieldComponent)
EmailSubjectField.displayName = "EmailSubjectField"
